import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Pie } from '@ant-design/plots';
import { Badge } from 'antd';
import classnames from 'classnames';

import { formatPieAnalyticsData } from 'utils/business-details';

import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';

import { ReactComponent as Pickups } from 'assets/bosta-icons/Pickups.svg';
import { ReactComponent as Location } from 'assets/bosta-icons/Location.svg';

import './StatisticsPie.less';

const StatisticsPie = ({
  rawData,
  referenceData,
  businessCount,
  locationsInfo,
  numberOfPickupLocations,
  DSR,
  isBusinessAnalysis = false,
  extraContent = null,
  loading = false,
  ...props
}) => {
  const intl = useIntl();
  const [pieData, setPieData] = useState([]);

  const citiesRankFormatting = () => {
    if (!locationsInfo) return intl.formatMessage({ id: 'common.empty_field' });

    const groupedCities = {};

    for (const { city_rank: rank, city_name: city } of locationsInfo) {
      if (!groupedCities[rank]) groupedCities[rank] = [];

      groupedCities[rank].push(city);
    }

    const rankedCitiesList = Object.entries(groupedCities).map(
      ([rank, city]) => {
        return { rank, city };
      }
    );

    const joinCityRank = rankedCitiesList.map(({ rank, city }, index) => {
      return `#${rank} ${city.length > 1 ? city.join(' & ') : city}`;
    });

    return joinCityRank.join(' - ');
  };

  useEffect(() => {
    if (!rawData) {
      setPieData([]);
      return;
    }

    const data = formatPieAnalyticsData(rawData, referenceData);
    setPieData(data);
  }, [rawData, referenceData]);

  const defaultPieData = [
    {
      type: intl.formatMessage({ id: 'merchant_analysis.no_data' }),
      value: 1,
      color: '#e4e7ec'
    }
  ];

  const dsrColorCode = () => {
    if (!DSR) return;

    const dsrPercentage = DSR * 100;

    if (dsrPercentage >= 0 && dsrPercentage <= 40) {
      return 'br-statistics-pie__dsr-red';
    } else if (dsrPercentage > 40 && dsrPercentage <= 80) {
      return 'br-statistics-pie__dsr-yellow';
    } else if (dsrPercentage > 80 && dsrPercentage <= 100) {
      return 'br-statistics-pie__dsr-green';
    }

    return '';
  };

  const config = {
    width: 300,
    height: 300,
    data: pieData?.length ? pieData : defaultPieData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    innerRadius: 0.8,
    color: pieData?.length
      ? pieData?.map((item) => item.color)
      : [defaultPieData[0].color],
    label: false,
    paddingAngle: 100,
    barWidth: 20,
    pieStyle: {
      lineCap: 'round'
    },
    interactions: [{ type: 'element-active' }],
    legend: false,
    statistic: false
  };

  const orderStatisticsInfo = [
    {
      icon: <Pickups />,
      label: intl.formatMessage({ id: 'merchant_analysis.pickup_locations' }),
      value: numberOfPickupLocations || 0
    },
    {
      icon: <Location />,
      label: intl.formatMessage({ id: 'merchant_analysis.delivered_areas' }),
      value: citiesRankFormatting()
    }
  ];

  const CustomLegend = useMemo(() => {
    if (!pieData || pieData.length === 0) {
      return (
        <div className="br-statistics-pie-legend">
          <Badge color="#e4e7ec" text="0 Delivered" style={{ fontSize: 14 }} />
        </div>
      );
    }

    return (
      <div className="br-statistics-pie-legend">
        {pieData.map((item) => (
          <Badge
            key={`${item.type}-${item.color}`}
            color={item.color}
            text={`${Number(item.value).toLocaleString()} ${item.type}`}
            style={{ fontSize: 14 }}
          />
        ))}
      </div>
    );
  }, [pieData]);

  return (
    <LoadingWrapper loading={loading}>
      <div className="br-statistics-pie-container">
        {extraContent && extraContent}
        <div
          className={classnames('br-statistics-pie__content', {
            'order-statistics-pie': isBusinessAnalysis
          })}
        >
          <div className="br-statistics-pie__content-pie">
            <div className="br-statistics-pie__content-pie-chart">
              <Pie {...config} />
              <div className="br-statistics-pie__content-pie-chart-title">
                <span className="display-xl">
                  {businessCount?.toLocaleString() || 0}
                </span>
                <span className="br-statistics-pie__content-pie-chart-title-subtitle subheading">
                  {pieData.length
                    ? DSR
                      ? intl.formatMessage({
                          id: 'merchant_analysis.total_orders'
                        })
                      : intl.formatMessage({
                          id: 'merchant_analysis.total_businesses'
                        })
                    : intl.formatMessage({ id: 'common.empty_field' })}
                </span>
                {!!DSR && (
                  <span
                    className={`br-statistics-pie__content-pie-chart-title-dsr ${dsrColorCode()} captions`}
                  >
                    {intl.formatMessage(
                      { id: 'merchant_analysis.dsr' },
                      { value: parseInt(DSR * 100) }
                    )}
                  </span>
                )}
              </div>
            </div>
            {CustomLegend}
          </div>

          {isBusinessAnalysis && (
            <div className="br-statistics-pie__content-details">
              {orderStatisticsInfo.map(({ icon, label, value }, index) => (
                <div
                  className="br-statistics-pie__content-details-info"
                  key={index}
                >
                  <span className="br-statistics-pie__content-details-info-label ">
                    {icon} {label}
                  </span>
                  <span className="br-statistics-pie__content-details-info-value">
                    {value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </LoadingWrapper>
  );
};

export default StatisticsPie;
